// Lib
import React from 'react';
import { Grid, Box } from '@mui/material';

// Components
import OrderInfoItem from '../../OrderQty/OrderInfo/OrderInfoItem';
import Feather from '../../../Feather';
import ActionButtons from '../ActionButtons';

// Hooks
import useUser from '../../../../hooks/useUser';
import useProduct from '../../../../pages/Approvals/hooks/useProduct';

import { useStyles } from './ProductCardStyles';

const DESCRIPTION_GRID_WIDTH_METHOD_PAGE = 12;
const DESCRIPTION_GRID_WIDTH_XS = 6;
const DESCRIPTION_GRID_WIDTH_SM = 8;
const DESCRIPTION_GRID_WIDTH_MD = 9;

const getDescriptionGridWidths = (isMethodPage) => {
  if (isMethodPage) {
    return {
      xs: DESCRIPTION_GRID_WIDTH_METHOD_PAGE,
      sm: DESCRIPTION_GRID_WIDTH_METHOD_PAGE,
      md: DESCRIPTION_GRID_WIDTH_METHOD_PAGE,
    };
  }
  return {
    xs: DESCRIPTION_GRID_WIDTH_XS,
    sm: DESCRIPTION_GRID_WIDTH_SM,
    md: DESCRIPTION_GRID_WIDTH_MD,
  };
};

const getStatusContainerLayout = (canShowActionButtons) => {
  if (canShowActionButtons) {
    return { display: 'block', alignItems: 'initial' };
  }
  return { display: 'flex', alignItems: 'center' };
};

const getStatusLabel = (status) => {
  if (status) {
    return status.toLowerCase();
  }
  return 'Pending Approval';
};

const ProductCard = ({ product, addressesCount, isMethodPage = false }) => {
  const {
    onOpenQtyModal,
    onOpenConfirmModal,
    addAction,
    canApproveProduct,
    isRejected,
    isAlreadyApproved,
  } = useProduct(product.product_id, product.qty_approved);

  const { isKiteUser } = useUser();
  const { classes } = useStyles({
    isKiteUser,
    canApproveProduct,
    isMethodPage,
  });
  const {
    product_description,
    product_id,
    product_managers,
    unit_measure,
    prepack_quantity,
    qty_approved,
    order_limit,
    monthly_limit,
    annual_limit,
    action,
    status,
  } = product || {};

  const totalApprovedQty = qty_approved * addressesCount;
  const descriptionGridWidths = getDescriptionGridWidths(isMethodPage);
  const canShowActionButtons = canApproveProduct && !isAlreadyApproved;
  const statusContainerLayout = getStatusContainerLayout(canShowActionButtons);

  const renderButtonStatus = () => {
    return (
      !isMethodPage && (
        <Grid
          display={statusContainerLayout.display}
          alignItems={statusContainerLayout.alignItems}
          className={classes.statusContainer}
          item
          xs={6}
          sm={4}
          md={3}
          borderBottom="1px solid #F4F4F4">
          {canShowActionButtons && (
            <ActionButtons
              className={classes.actionButtons}
              status={action}
              onAccept={addAction}
              onReject={onOpenConfirmModal}
            />
          )}
          {!canShowActionButtons && (
            <div className={classes.status}>{getStatusLabel(status)}</div>
          )}
        </Grid>
      )
    );
  };
  const renderEditButton = () => {
    return (
      !(isRejected || isAlreadyApproved) &&
      !isMethodPage &&
      canApproveProduct && (
        <Feather
          className={classes.editIcon}
          type="edit"
          onClick={onOpenQtyModal}
        />
      )
    );
  };

  return (
    <Grid container className={classes.root} spacing={1}>
      <Grid
        display="flex"
        alignItems="center"
        item
        pt={isMethodPage && { md: '16px !important' }}
        pb={isMethodPage && { md: 1.5 }}
        xs={descriptionGridWidths.xs}
        sm={descriptionGridWidths.sm}
        md={descriptionGridWidths.md}
        borderBottom="1px solid #F4F4F4">
        <OrderInfoItem
          className={classes.description}
          title="Product description"
          text={product_description}
        />
      </Grid>
      {renderButtonStatus()}
      <Grid item xs={5} md={5.5} borderBottom="1px solid #F4F4F4">
        <OrderInfoItem
          className={classes.orderInfo}
          title="Product Manager"
          text={product_managers?.join(', ') || ''}
        />
        <OrderInfoItem
          className={classes.orderInfo}
          title="Unit of Measure"
          text={unit_measure}
        />
        <OrderInfoItem
          className={classes.orderInfo}
          title="Per Pack Qty"
          text={prepack_quantity}
        />
      </Grid>
      <Grid item xs={3} md={3.5} borderBottom="1px solid #F4F4F4">
        <Box display="flex">
          <OrderInfoItem
            className={!isMethodPage && classes.orderQty}
            title="Order Qty"
            text={qty_approved}
          />
          {renderEditButton()}
        </Box>
        {addressesCount > 1 && (
          <OrderInfoItem
            className={classes.orderInfo}
            title="Total Qty"
            text={totalApprovedQty}
          />
        )}
        <OrderInfoItem
          className={classes.orderInfo}
          title="Stock number"
          text={product_id}
        />
      </Grid>
      <Grid
        ml={{ md: 0, xs: 1.5 }}
        item
        xs={3}
        md={3}
        borderBottom="1px solid #F4F4F4">
        <OrderInfoItem
          className={classes.orderInfo}
          title="Max order Qty"
          text={order_limit}
        />
        <OrderInfoItem
          className={classes.orderInfo}
          title="Monthly limit"
          text={monthly_limit}
        />
        <OrderInfoItem
          className={classes.orderInfo}
          title="Annual limit"
          text={annual_limit}
        />
      </Grid>
    </Grid>
  );
};

export default ProductCard;
